import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { IUserRef } from "../../models/userData.model";
import {
  getOneRepetitiveTask,
  getOnetaskById,
  getTaskMetaData,
  updateRepetitiveTaskAttachments,
  updateRepetitiveTaskManager,
  updateStopRepetitiveTask,
  updateTaskAttachments,
  updateTaskManagementStatus,
  updateTaskManager,
  updateTaskManagerComment,
} from "../../store/slices/taskmanagementSlice";
import TaskManagerCommentModal from "./TaskManagerCommentModal";
import { fetchUsers } from "../../store/slices/userSlice";
import {
  IRepetitiveTaskManager,
  ITaskManagement,
  RepetitiveTaskDays,
  RepetitiveTaskType,
} from "../../models/taskManagement.model";
import {
  buildFieldChanges,
  FormChangeEvent,
  handleFormChangeUtil,
  removeAttachment,
} from "../../utils/taskManagementUtils";
import toast from "react-hot-toast";
import { formatDate } from "../../utils/formatDateUtils";
import TaskManagerAttachment from "./TaskManagerAttachment";
import TaskManagerComments from "./TaskManagerComments";
import TaskDetailsPanel from "./TaskManagerPanel";
import Loading from "../AssetsComponent/loading";
import TaskManagerTimeInputModal from "./TaskManagerTimeInputModal";

const TaskManagerDetail: React.FC = () => {
  const { taskId } = useParams();
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const dispatch = useDispatch<RootDispatch>();

  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [, setRemovingAttachment] = useState<string | null>(null);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const [form, setForm] = useState<ITaskManagement>({
    taskName: "",
    assignee: "",
    followers: [] as string[],
    type: "" as string,
    description: "",
    priority: "" as string,
    status: "ongoing",
    startDate: null,
    endDate: null,
  });

  const isRepetitiveTask = searchParams.get("type") === "repetitive";

  const currentTask = useSelector(
    (state: RootState) => state.TaskManagement.currentTask
  );

  const currentRepetitiveTask = useSelector(
    (state: RootState) => state.TaskManagement.currentRepetitiveTask
  );

  const [repetitionType, setRepetitionType] = useState<RepetitiveTaskType>(
    (currentRepetitiveTask?.typeOfRepetition?.type as RepetitiveTaskType) ||
      RepetitiveTaskType.weekly
  );
  const [selectedDays, setSelectedDays] = useState<RepetitiveTaskDays[]>(
    (currentRepetitiveTask?.typeOfRepetition?.data
      ?.days as RepetitiveTaskDays[]) || []
  );
  const [monthlyDate, setMonthlyDate] = useState<string>(
    currentRepetitiveTask?.typeOfRepetition?.data?.date || ""
  );
  const [yearlyDate, setYearlyDate] = useState<string>(
    currentRepetitiveTask?.typeOfRepetition?.data?.date || ""
  );
  const [noOfDays, setNoOfDays] = useState<number>(
    currentRepetitiveTask?.typeOfRepetition?.data?.noOfDays || 0
  );

  const users = useSelector((state: RootState) => state.users.user);
  const metadata = useSelector(
    (state: RootState) => state.TaskManagement.metadata
  );
  const rawTypes = metadata?.type;
  const rawPriorities = metadata?.priority;

  const types = Array.isArray(rawTypes) ? rawTypes[0] : rawTypes || {};
  const priorities = Array.isArray(rawPriorities)
    ? rawPriorities[0]
    : rawPriorities || {};

  const displayTask = isRepetitiveTask ? currentRepetitiveTask : currentTask;

  useEffect(() => {
    if (taskId) {
      if (isRepetitiveTask) {
        dispatch(getOneRepetitiveTask({ taskId }));
      } else {
        dispatch(getOnetaskById({ taskId }));
      }
    }
  }, [dispatch, taskId, isRepetitiveTask]);

  useEffect(() => {
    if (displayTask) {
      setForm({
        taskName: displayTask.taskName || "",
        assignee: (displayTask.assignee as unknown as IUserRef)?._id || "",
        followers: (displayTask.followers || []).map((f: any) =>
          typeof f === "string" ? f : f._id
        ),
        type: displayTask.type || "",
        description: displayTask.description || "",
        priority: displayTask.priority || "",
        status: displayTask.status || "ongoing",
        startDate: displayTask.startDate || null,
        endDate: displayTask.endDate || null,
        attachments: displayTask.attachments || [],
        typeOfRepetition: (displayTask as any).typeOfRepetition ?? null,
      });
      if (isRepetitiveTask && displayTask.typeOfRepetition) {
        const repetition = displayTask.typeOfRepetition;
        setRepetitionType(repetition.type as RepetitiveTaskType);

        if (repetition.type === RepetitiveTaskType.weekly) {
          setSelectedDays(repetition.data?.days || []);
        } else if (repetition.type === RepetitiveTaskType.monthly) {
          setMonthlyDate(repetition.data?.date || "");
        } else if (repetition.type === RepetitiveTaskType.yearly) {
          setYearlyDate(repetition.data?.date || "");
        }

        setNoOfDays(repetition.data?.noOfDays || 0);
      }
    }
  }, [displayTask, isRepetitiveTask]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFiles(event.target.files);
  };

  useEffect(() => {
    dispatch(fetchUsers());
    dispatch(getTaskMetaData());
  }, [dispatch]);

  const handleFileUpload = async () => {
    if (!selectedFiles || !taskId) return;

    const formData = new FormData();
    Array.from(selectedFiles).forEach((file) =>
      formData.append("attachments", file as File)
    );

    try {
      if (isRepetitiveTask) {
        await dispatch(
          updateRepetitiveTaskAttachments({ taskId, payload: formData })
        ).unwrap();

        await dispatch(getOneRepetitiveTask({ taskId }));
      } else {
        await dispatch(
          updateTaskAttachments({ taskId, payload: formData })
        ).unwrap();
        await dispatch(getOnetaskById({ taskId }));
      }
      setSelectedFiles(null);
      const input = document.getElementById(
        "fileUploadInput"
      ) as HTMLInputElement;
      if (input) input.value = "";
    } catch (err) {
      console.error("Failed to upload attachments:", err);
    }
  };

  const handleRemoveAttachment = async (attachmentId: string) => {
    if (!taskId) return;

    try {
      setRemovingAttachment(attachmentId);
      await removeAttachment(dispatch, taskId, attachmentId, isRepetitiveTask);
      if (isRepetitiveTask) {
        await dispatch(getOneRepetitiveTask({ taskId }));
      } else {
        await dispatch(getOnetaskById({ taskId }));
      }
      setRemovingAttachment(null);
    } catch (err) {
      setRemovingAttachment(null);
    }
  };

  const handleAddComment = async (mentions: string[], comment: string) => {
    if (!taskId) return;
    try {
      await dispatch(
        updateTaskManagerComment({ taskId, mentions, comment })
      ).unwrap();
      await dispatch(getOnetaskById({ taskId }));
    } catch (err) {
      console.error("Failed to add comment:", err);
    }
  };

  const handleChange = (e: FormChangeEvent) => {
    handleFormChangeUtil(e, form, setForm);
  };

  const handleRepetitionTypeChange = (type: RepetitiveTaskType) => {
    setRepetitionType(type);
  };

  const handleDaySelection = (day: RepetitiveTaskDays) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleDaySelectionAll = (select: boolean) => {
    if (select) {
      setSelectedDays(
        Object.values(RepetitiveTaskDays).filter(
          (day) => day !== RepetitiveTaskDays.sunday
        )
      );
    } else {
      setSelectedDays([]);
    }
  };

  const handleUpdateTask = async () => {
    if (!taskId || !displayTask) return;

    if (!form.taskName.trim()) {
      toast.error("Task Name is required.");
      return;
    }
    if (!form.assignee) {
      toast.error("Please select an assignee.");
      return;
    }
    if (!form.type) {
      toast.error("Please select a task type.");
      return;
    }
    if (!form.priority) {
      toast.error("Please select a task priority.");
      return;
    }
    if (!form.startDate) {
      toast.error("Please select a start date.");
      return;
    }

    let completeData: Partial<ITaskManagement | IRepetitiveTaskManager> = {
      ...form,
    };

    if (isRepetitiveTask) {
      let repetitionData: any = {};

      if (
        repetitionType === RepetitiveTaskType.weekly &&
        selectedDays.length > 0
      ) {
        repetitionData = { days: selectedDays, noOfDays };
      } else if (repetitionType === RepetitiveTaskType.monthly && monthlyDate) {
        // DD format
        const day = new Date(monthlyDate).getDate().toString();
        repetitionData = { date: day, noOfDays };
      } else if (repetitionType === RepetitiveTaskType.yearly && yearlyDate) {
        // MM-DD format
        const dateObj = new Date(yearlyDate);
        const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
        const day = dateObj.getDate().toString().padStart(2, "0");
        repetitionData = { date: `${month}-${day}`, noOfDays };
      }

      completeData = {
        ...completeData,
        typeOfRepetition: {
          type: repetitionType,
          data: repetitionData,
        },
      };
    }

    const changes = buildFieldChanges(displayTask, completeData);
    if (changes.length === 0) {
      toast("No changes detected");
      return;
    }

    try {
      if (isRepetitiveTask) {
        await dispatch(
          updateRepetitiveTaskManager({
            id: taskId,
            data: completeData as IRepetitiveTaskManager,
            changes,
          })
        ).unwrap();
        await dispatch(getOneRepetitiveTask({ taskId }));
      } else {
        await dispatch(
          updateTaskManager({
            id: taskId,
            data: completeData as ITaskManagement,
            changes,
          })
        ).unwrap();
        await dispatch(getOnetaskById({ taskId }));
      }
      setEditMode(false);
    } catch (error) {
      console.error("Failed to update task:", error);
      toast.error("Failed to update task");
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    if (currentTask) {
      setForm({
        taskName: currentTask.taskName || "",
        assignee: (currentTask.assignee as unknown as IUserRef)?._id || "",
        followers: currentTask.followers?.map((f: any) => f._id) || [],
        type: currentTask.type || "",
        description: currentTask.description || "",
        priority: currentTask.priority || "",
        status: currentTask.status || "ongoing",
        startDate: currentTask.startDate || null,
        endDate: currentTask.endDate || null,
      });
    }
  };

  const updateStatusAndTime = async (time: string) => {
    if (taskId) {
      await dispatch(
        updateTaskManagementStatus({
          taskId: taskId,
          approxTime: time,
        })
      ).unwrap();

      if (isRepetitiveTask) {
        await dispatch(getOneRepetitiveTask({ taskId }));
      } else {
        await dispatch(getOnetaskById({ taskId }));
      }
    }
    setIsTimeModalOpen(false);
  };

  const handleStopRepetitiveTask = async () => {
    if (!taskId) return;

    try {
      await dispatch(updateStopRepetitiveTask({ taskId })).unwrap();

      if (isRepetitiveTask) {
        await dispatch(getOneRepetitiveTask({ taskId }));
      }
    } catch (error) {
      console.error("Failed to stop repetitive task:", error);
    }
  };

  const handleOpenCompleteModal = () => {
    setIsTimeModalOpen(true);
  };

  if (!displayTask) {
    return <Loading />;
  }

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase();

    if (statusLower === "completed") {
      return "text-emerald-700 ";
    } else if (statusLower === "waiting for approval") {
      return "text-amber-700 ";
    } else if (statusLower === "ongoing") {
      return "text-blue-700";
    } else {
      return "text-gray-700 ";
    }
  };

  const getPriorityColor = (priority: string) => {
    const priorityLower = priority?.toLowerCase();

    if (priorityLower === "hot") {
      return "text-red-700";
    } else if (priorityLower === "warm") {
      return "text-yellow-700 ";
    } else if (priorityLower === "cold") {
      return "text-emerald-700";
    } else {
      return "text-gray-700";
    }
  };

  const formatDateForInput = (date?: Date | string) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  const inputBaseClasses =
    "w-full p-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 outline-none bg-white text-gray-900 placeholder-gray-500";
  const selectClasses =
    "w-full p-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 outline-none bg-white text-gray-900 cursor-pointer";
  const labelClasses = "block text-xs font-semibold text-gray-700 mb-2";

  return (
    <div className="min-h-screen ">
      <div className="max-w-8xl mx-auto p-6 text-xs">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <TaskDetailsPanel
              currentTask={displayTask}
              editMode={editMode}
              setEditMode={setEditMode}
              form={form}
              setForm={setForm}
              handleChange={handleChange}
              handleUpdateTask={handleUpdateTask}
              handleCancelEdit={handleCancelEdit}
              inputBaseClasses={inputBaseClasses}
              selectClasses={selectClasses}
              labelClasses={labelClasses}
              types={types}
              priorities={priorities}
              users={users}
              getStatusColor={getStatusColor}
              getPriorityColor={getPriorityColor}
              formatDate={formatDate}
              formatDateForInput={formatDateForInput}
              navigate={navigate}
              repetitionType={repetitionType}
              handleRepetitionTypeChange={handleRepetitionTypeChange}
              selectedDays={selectedDays}
              handleDaySelection={handleDaySelection}
              handleDaySelectionAll={handleDaySelectionAll}
              monthlyDate={monthlyDate}
              setMonthlyDate={setMonthlyDate}
              yearlyDate={yearlyDate}
              setYearlyDate={setYearlyDate}
              noOfDays={noOfDays}
              setNoOfDays={setNoOfDays}
              onOpenCompleteModal={handleOpenCompleteModal}
              handleStopRepetitiveTask={handleStopRepetitiveTask}
            />
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            <TaskManagerAttachment
              attachments={displayTask?.attachments || []}
              selectedFiles={selectedFiles}
              inputId="fileUploadInput"
              onFileSelect={handleFileSelect}
              onUpload={handleFileUpload}
              onRemoveAttachment={handleRemoveAttachment}
              showUpdateButton={!!selectedFiles}
            />

            {!isRepetitiveTask && (
              <TaskManagerComments
                comments={(currentTask?.comments || []).map((c: any) => ({
                  ...c,
                  mentions: (c.mentions || []).map((m: any) =>
                    typeof m === "string" ? { _id: m, username: "" } : m
                  ),
                }))}
                onAddClick={() => setIsCommentModalOpen(true)}
              />
            )}
          </div>
        </div>
      </div>
      <TaskManagerTimeInputModal
        isOpen={isTimeModalOpen}
        onClose={() => setIsTimeModalOpen(false)}
        onSubmit={updateStatusAndTime}
      />
      <TaskManagerCommentModal
        isOpen={isCommentModalOpen}
        users={users}
        onClose={() => setIsCommentModalOpen(false)}
        handleAddComment={handleAddComment}
      />
    </div>
  );
};

export default TaskManagerDetail;
